import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import type { Stripe, StripeCheckoutElementsSdk } from '@stripe/stripe-js';
import { environment } from '../../environments/environment';
import { PAYMENT_CONFIG } from './payment-config';

/**
 * Task 1 deliberately did not publish this shape to `@kin/property-tax-types`, so it is
 * declared locally. A types bump can replace it later; nothing here should be published.
 */
export interface CheckoutSessionResponse {
  clientSecret: string;
}

/** `GET /payments/status`. Local for the same reason: nothing here is published. */
export interface PaymentStatusResponse {
  status: 'pending' | 'paid' | 'failed';
  amountCents: number;
  currency: string;
  paidAt: string | null;
}

@Injectable({ providedIn: 'root' })
export class PaymentService {
  private http = inject(HttpClient);
  private config = inject(PAYMENT_CONFIG);

  private stripe: Promise<Stripe | null> | null = null;

  /**
   * Empty body on purpose: amount, currency and product are server-side constants, and
   * anything posted here would be ignored. Mirrors `savePartnerAgreement()`'s shape.
   */
  createCheckoutSession(): Observable<CheckoutSessionResponse> {
    return this.http.post<CheckoutSessionResponse>(`${environment.apiUrl}/payments/checkout-session`, {});
  }

  /**
   * Resolves one Checkout Session for the current user. The endpoint reconciles with Stripe
   * itself, so a single call is authoritative even if the webhook has not landed — never poll
   * it. 404 covers both an unknown session and another user's; the caller treats them alike.
   */
  getPaymentStatus(sessionId: string): Observable<PaymentStatusResponse> {
    return this.http.get<PaymentStatusResponse>(`${environment.apiUrl}/payments/status`, {
      params: { sessionId },
    });
  }

  /**
   * Both the loader chunk and `js.stripe.com` are only touched from here, and this is only
   * reached with the flag on. `/pure` rather than the package root: the root injects the
   * script as an import side effect, which would defeat the lazy load. Stripe.js is always
   * fetched from Stripe's origin — never bundled or self-hosted, for PCI compliance.
   */
  private loadStripe(): Promise<Stripe | null> {
    this.stripe ??= import('@stripe/stripe-js/pure').then((m) =>
      m.loadStripe(this.config.stripePublishableKey),
    );
    return this.stripe;
  }

  /** Elements integration (`ui_mode: 'elements'`), the HTML + JS path. */
  async initCheckout(clientSecret: string): Promise<StripeCheckoutElementsSdk> {
    const stripe = await this.loadStripe();
    if (!stripe) throw new Error('Stripe.js failed to load');
    return stripe.initCheckoutElementsSdk({ clientSecret });
  }
}
